/**
 * TanStack Query hooks for grants (capability sets placed on a study or DTA).
 */

import { type InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query"
import type { GrantListResponse } from "@/generated/sdk"

import { type GrantItem, type GrantTarget, grantsApi } from "@/services/grantsApi"

import { useAllPages } from "./useAllPages"

type GrantKey = Pick<GrantItem, "principal_id" | "capability_set">

export const grantKeys = {
  all: ["grants"] as const,
  lists: () => [...grantKeys.all, "list"] as const,
  list: (target: GrantTarget) => [...grantKeys.lists(), target.type, target.id] as const,
  grantCandidateLists: () => [...grantKeys.all, "grantCandidates"] as const,
  grantCandidates: (dtaId: string) => [...grantKeys.grantCandidateLists(), dtaId] as const,
}

type GrantPages = InfiniteData<GrantListResponse, string | null>

/**
 * Fetch every grant placed directly on a resource, and whether the caller may grant and revoke
 * there.
 */
export function useGrantsList(target: GrantTarget, pageSize = 20) {
  const { items, firstPage, isPending, error } = useAllPages(grantKeys.list(target), (nextToken) =>
    grantsApi.listGrants(target, { limit: pageSize, nextToken }),
  )
  return { grants: items, canManage: firstPage?.can_manage ?? false, isPending, error }
}

/**
 * Fetch the DTA's vendor company users as grantee suggestions. Callers without `read_vendor` on the
 * DTA get a 403.
 */
export function useDtaGrantCandidates(dtaId: string, pageSize = 50) {
  const { items, isPending, error } = useAllPages(grantKeys.grantCandidates(dtaId), (nextToken) =>
    grantsApi.listDtaGrantCandidates(dtaId, { limit: pageSize, nextToken }),
  )
  return { candidates: items, isPending, error }
}

/**
 * Grant a person a capability set on a resource and invalidate its grants list.
 */
export function useCreateGrant(target: GrantTarget) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: { email: string; capability_set: string }) =>
      grantsApi.createGrant(target, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: grantKeys.list(target) })
    },
  })
}

/**
 * Revoke a grant, removing it from the cached list optimistically.
 */
export function useRevokeGrant(target: GrantTarget) {
  const queryClient = useQueryClient()
  const key = grantKeys.list(target)
  return useMutation({
    mutationFn: (grant: GrantKey) =>
      grantsApi.revokeGrant(target, grant.principal_id, grant.capability_set),
    onMutate: async (grant) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<GrantPages>(key)
      queryClient.setQueryData<GrantPages>(key, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                items: page.items.filter((g) => !isSameGrant(g, grant)),
              })),
            }
          : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

function isSameGrant(a: GrantKey, b: GrantKey): boolean {
  return a.principal_id === b.principal_id && a.capability_set === b.capability_set
}
